import { useCurrency } from "../../currency/CurrencyContext";
import type { PriceLevel } from "../types";

interface LadderMarket {
  marketName: string;
  marketIconUrl: string | null;
  marketLink: string | null;
  levels: PriceLevel[];
  selectedPrice: number;
  onPriceSelect: (price: number) => void;
}

interface PriceLadderProps {
  buy: LadderMarket;
  sell: LadderMarket;
}

interface DisplayPriceLevel extends PriceLevel {
  isSelectedOnly?: boolean;
}

function formatCount(value: number): string {
  return new Intl.NumberFormat("ru-RU").format(value);
}

function levelsWithSelectedPrice(
  levels: PriceLevel[],
  selectedPrice: number,
  tone: "buy" | "sell",
): DisplayPriceLevel[] {
  const hasSelectedPrice = levels.some(
    (level) => Math.abs(level.price - selectedPrice) < 0.000001,
  );
  if (hasSelectedPrice) {
    return levels;
  }

  return [
    ...levels,
    { price: selectedPrice, offers_count: 0, isSelectedOnly: true },
  ].sort((left, right) =>
    tone === "buy" ? left.price - right.price : right.price - left.price,
  );
}

function MarketIdentity({
  market,
  tone,
}: {
  market: LadderMarket;
  tone: "buy" | "sell";
}) {
  const { formatPrice } = useCurrency();
  return (
    <div className={`market-depth-identity market-depth-identity-${tone}`}>
      <div className="market-depth-market">
        {market.marketIconUrl ? (
          <img src={market.marketIconUrl} alt="" />
        ) : (
          <span className="market-logo-fallback">
            {market.marketName.slice(0, 1)}
          </span>
        )}
        <div>
          <span>{tone === "buy" ? "Покупка" : "Продажа"}</span>
          {market.marketLink ? (
            <a
              className="market-name-link"
              href={market.marketLink}
              target="_blank"
              rel="noopener noreferrer"
              title={`Открыть ${market.marketName}`}
              onClick={(event) => event.stopPropagation()}
            >
              <strong>{market.marketName}</strong>
            </a>
          ) : (
            <strong title={market.marketName}>{market.marketName}</strong>
          )}
        </div>
      </div>
      <div className="market-depth-selected-price">
        <small>Выбрано</small>
        <strong>{formatPrice(market.selectedPrice)}</strong>
      </div>
    </div>
  );
}

function DepthLevel({
  level,
  tone,
  selectedPrice,
  maxCount,
  onPriceSelect,
}: {
  level: DisplayPriceLevel | undefined;
  tone: "buy" | "sell";
  selectedPrice: number;
  maxCount: number;
  onPriceSelect: (price: number) => void;
}) {
  const { formatPrice } = useCurrency();
  if (!level) {
    return <span className={`market-depth-level is-empty market-depth-level-${tone}`} />;
  }

  const isSelected = Math.abs(level.price - selectedPrice) < 0.000001;
  const fillWidth =
    level.offers_count > 0
      ? Math.max(7, (level.offers_count / maxCount) * 100)
      : 0;

  return (
    <button
      className={`market-depth-level market-depth-level-${tone}${
        isSelected ? " is-selected" : ""
      }`}
      type="button"
      aria-pressed={isSelected}
      title={`Использовать ${formatPrice(level.price)} как цену ${
        tone === "buy" ? "покупки" : "продажи"
      }`}
      onClick={() => onPriceSelect(level.price)}
    >
      <span
        className="market-depth-fill"
        style={{ width: `${fillWidth}%` }}
      />
      <strong>{formatPrice(level.price)}</strong>
      <span>{level.isSelectedOnly ? "—" : formatCount(level.offers_count)}</span>
    </button>
  );
}

export function PriceLadder({ buy, sell }: PriceLadderProps) {
  const buyLevels = levelsWithSelectedPrice(
    buy.levels,
    buy.selectedPrice,
    "buy",
  ).slice(0, 7);
  const sellLevels = levelsWithSelectedPrice(
    sell.levels,
    sell.selectedPrice,
    "sell",
  ).slice(0, 7);
  const rowCount = Math.max(buyLevels.length, sellLevels.length, 1);
  const buyMaxCount = Math.max(
    ...buyLevels.map((level) => level.offers_count),
    1,
  );
  const sellMaxCount = Math.max(
    ...sellLevels.map((level) => level.offers_count),
    1,
  );

  return (
    <section className="market-depth-card" aria-label="Лестница цен сделки">
      <div className="market-depth-route">
        <MarketIdentity market={buy} tone="buy" />
        <MarketIdentity market={sell} tone="sell" />
      </div>

      <div className="market-depth-column-heads" aria-hidden="true">
        <span>
          <i>Цена</i>
          <i>Офферы</i>
        </span>
        <span>
          <i>Цена</i>
          <i>Офферы</i>
        </span>
      </div>

      <div className="market-depth-rows">
        {Array.from({ length: rowCount }, (_, index) => (
          <div className="market-depth-row" key={index}>
            <DepthLevel
              level={buyLevels[index]}
              tone="buy"
              selectedPrice={buy.selectedPrice}
              maxCount={buyMaxCount}
              onPriceSelect={buy.onPriceSelect}
            />
            <DepthLevel
              level={sellLevels[index]}
              tone="sell"
              selectedPrice={sell.selectedPrice}
              maxCount={sellMaxCount}
              onPriceSelect={sell.onPriceSelect}
            />
          </div>
        ))}
      </div>
    </section>
  );
}
